import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Flex,
  useDisclosure,
  Alert,
  AlertIcon,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { FaEdit } from "react-icons/fa";
import { BASE_URL } from "../../../../Globalapi/Globalapi";

const EditModalForSchoolMember = ({ memberId, userId, schoolId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formData, setFormData] = useState({
    amount: "",
    is_paid: "",
    amount_return: "",
    is_active: "1", // Default active status
    name: "", // Name of the member
    role: "", // Role of the member (student/teacher)
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      console.log("Modal opened. Fetching member data...");
      fetchMemberData();
    }
  }, [isOpen]);

  const fetchMemberData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching data for member ID: ${memberId}`);
      const response = await fetch(
        `${BASE_URL}/admin/library/membership/view_member.php?member_id=${memberId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch member data.");
      }

      const data = await response.json();
      console.log("Fetched member data:", data);

      if (data.success) {
        setFormData({
          amount: data.data.amount || "",
          is_paid: String(data.data.is_paid) || "",
          amount_return: data.data.amount_return || "",
          is_active: String(data.data.is_active) || "1",
          name: data.data.member_name || "", // Corrected to use member_name
          role: data.data.member_type || "", // Corrected to use member_type
        });
      } else {
        setError(data.message || "Failed to load member details.");
      }
    } catch (err) {
      console.error("Error while fetching member data:", err);
      setError(err.message || "An error occurred while fetching member data.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Updating form field: ${name}, Value: ${value}`);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const updatedData = {
      user_id: userId,
      member_id: memberId,
      amount: formData.amount,
      is_paid: formData.is_paid,
      amount_return: formData.amount_return,
      is_active: formData.is_active,
      school_id: schoolId,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/membership/edit_member.php`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      const result = await response.json();
      if (result.success) {
        console.log("Member updated successfully:", result);
        onClose(); // Close the modal on success
      } else {
        setError(result.message || "Failed to update member.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while updating member.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" colorScheme="blue" onClick={onOpen}>
        Edit
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Member
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {loading ? (
              <Spinner />
            ) : error ? (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <Flex direction="column">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {/* First Row: Name & Role */}
                    <FormControl mb={4} isRequired>
                      <FormLabel>Name</FormLabel>
                      <Input
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="name"
                        type="text"
                        value={formData.name}
                        readOnly
                        placeholder="Member Name"
                      />
                    </FormControl>

                    <FormControl mb={4}>
                      <FormLabel>Role</FormLabel>
                      <Input
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="role"
                        type="text"
                        value={formData.role}
                        readOnly
                        placeholder="Role (Student/Teacher)"
                      />
                    </FormControl>

                    {/* Second Row: Amount & Paid Status (Fixed Alignment) */}
                    <FormControl mb={4} isRequired>
                      <FormLabel>Amount</FormLabel>
                      <Input
                      mt={0}
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="amount"
                        type="number"
                        value={formData.amount}
                        onChange={handleChange}
                        placeholder="Enter amount"
                      />
                    </FormControl>

                    <FormControl mb={4}>
                      <FormLabel>Paid Status</FormLabel>
                      <Select
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="is_paid"
                        value={formData.is_paid}
                        onChange={handleChange}
                      >
                        <option value="1">Paid</option>
                        <option value="0">Unpaid</option>
                      </Select>
                    </FormControl>

                    {/* Third Row: Amount Return & Active Status */}
                    <FormControl mb={4}>
                      <FormLabel>Amount Return</FormLabel>
                      <Input
                      mt={0}
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="amount_return"
                        type="number"
                        value={formData.amount_return}
                        onChange={handleChange}
                        placeholder="Enter amount return"
                      />
                    </FormControl>

                    <FormControl mb={4}>
                      <FormLabel>Active Status</FormLabel>
                      <Select
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        name="is_active"
                        value={formData.is_active}
                        onChange={handleChange}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>
                </Flex>

                <ModalFooter>
                  {/* <Button variant="ghost" onClick={onClose}>
                    Cancel
                  </Button> */}
                  <Button
                    bgGradient="linear(to-r, teal.400, teal.600)"
                    color="white"
                    _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
                    type="submit"
                    ml={3}
                  >
                    Update
                  </Button>
                </ModalFooter>
              </form>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditModalForSchoolMember;
